import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { SortableHeader } from '@/components/SortableHeader';
import { 
  History, 
  Search,
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  User,
  Calendar,
  Filter,
  RefreshCw
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_CONFIG = {
  CREATE: { label: 'Created', color: 'bg-green-100 text-green-700', icon: Plus },
  UPDATE: { label: 'Updated', color: 'bg-blue-100 text-blue-700', icon: Pencil },
  DELETE: { label: 'Deleted', color: 'bg-red-100 text-red-700', icon: Trash2 },
};

const RECORD_TYPES = [
  'Customer',
  'Job',
  'Timesheet',
  'LaborEntry',
  'VendorInvoice',
  'CustomerInvoice',
  'MaterialPO',
  'CrewMember',
  'Craft',
  'Classification',
];

export default function AuditLog() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filter state
  const [recordTypeFilter, setRecordTypeFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');
  const [userFilter, setUserFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [logsRes, usersRes] = await Promise.all([
        axios.get(`${API}/audit-logs`, {
          headers: { Authorization: `Bearer ${token}` },
          params: buildFilterParams(),
        }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const buildFilterParams = () => {
    const params = {};
    if (recordTypeFilter !== 'ALL') params.record_type = recordTypeFilter;
    if (userFilter !== 'ALL') params.user_id = userFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return params;
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleClearFilters = () => {
    setRecordTypeFilter('ALL');
    setUserFilter('ALL');
    setStartDate('');
    setEndDate('');
    setTimeout(fetchData, 0);
  };

  const toggleRow = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      if (isValid(date)) {
        return format(date, 'MMM d, yyyy h:mm a');
      }
    } catch (e) {
      console.warn('Date parse failed for:', timestamp, e?.message);
    }
    return timestamp;
  };

  const handleSort = (column) => {
    if (sortColumn === column) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDir('asc'); }
  };

  const sortedLogs = useMemo(() => {
    let result = [...logs];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        l.userName?.toLowerCase().includes(q) ||
        l.action?.toLowerCase().includes(q) ||
        l.recordType?.toLowerCase().includes(q) ||
        l.recordDescription?.toLowerCase().includes(q)
      );
    }
    if (sortColumn) {
      result.sort((a, b) => {
        const aVal = a[sortColumn] || '';
        const bVal = b[sortColumn] || '';
        return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
      });
    }
    return result;
  }, [logs, searchQuery, sortColumn, sortDir]);

  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return <span className="text-slate-400 italic">empty</span>;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-sm text-slate-500 italic">No detailed changes recorded</p>;
    }

    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([field, change]) => {
          // Handle special case where change is not an object with old/new
          if (typeof change !== 'object' || change === null) {
            return (
              <div key={field} className="flex items-start gap-2 text-sm">
                <span className="font-medium text-slate-700 min-w-[120px]">{field}:</span>
                <span className="text-slate-600">{formatFieldValue(change)}</span>
              </div>
            );
          }

          return (
            <div key={field} className="flex items-start gap-2 text-sm">
              <span className="font-medium text-slate-700 min-w-[120px]">{field}:</span>
              <div className="flex items-center gap-2">
                <span className="text-red-600 line-through bg-red-50 px-1 rounded">
                  {formatFieldValue(change.old)}
                </span>
                <span className="text-slate-400">→</span>
                <span className="text-green-600 bg-green-50 px-1 rounded">
                  {formatFieldValue(change.new)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 overflow-x-hidden" data-testid="audit-log-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 break-words">Audit Log</h1>
        <p className="mt-2 text-lg text-slate-700">Track all system changes and user actions</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm rounded-sm mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:flex md:flex-wrap gap-4 items-end">
            <div className="col-span-1">
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1">Record Type</Label>
              <Select value={recordTypeFilter} onValueChange={setRecordTypeFilter}>
                <SelectTrigger className="rounded-sm" data-testid="record-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Types</SelectItem>
                  {RECORD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-1">
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1">User</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="rounded-sm" data-testid="user-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-1">
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-sm"
                data-testid="start-date-filter"
              />
            </div>

            <div className="col-span-1">
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-sm"
                data-testid="end-date-filter"
              />
            </div>

            <div className="col-span-2 flex gap-2 mt-2 md:mt-0">
              <Button
                onClick={handleApplyFilters}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white rounded-sm flex-1 md:flex-none"
                data-testid="apply-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>

              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="rounded-sm flex-1 md:flex-none"
                data-testid="clear-filters-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-800">{logs.length}</p>
            <p className="text-xs text-slate-500 uppercase">Total Entries</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {logs.filter(l => l.action === 'CREATE').length}
            </p>
            <p className="text-xs text-slate-500 uppercase">Created</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {logs.filter(l => l.action === 'UPDATE').length}
            </p>
            <p className="text-xs text-slate-500 uppercase">Updated</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {logs.filter(l => l.action === 'DELETE').length}
            </p>
            <p className="text-xs text-slate-500 uppercase">Deleted</p>
          </CardContent>
        </Card>
      </div>

      {/* Audit Log Table */}
      {logs.length > 0 && (
        <div className='mb-4'>
          <div className='relative max-w-xs'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <Input data-testid='audit-search' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search audit logs...' className='pl-9 rounded-sm border-2' />
          </div>
        </div>
      )}

      {logs.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <History className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600">No audit log entries found</p>
            <p className="text-sm text-slate-500 mt-2">
              Changes to records will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm rounded-sm overflow-hidden">
          <div className="overflow-x-auto min-w-0">
          <Table className="min-w-[600px]" data-testid='audit-table'>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10"></TableHead>
                <SortableHeader column='timestamp' label='Timestamp' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='userName' label='User' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='action' label='Action' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader column='recordType' label='Record Type' sortColumn={sortColumn} sortDir={sortDir} onSort={handleSort} />
                <TableHead>Record</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLogs.map((log) => {
                const ActionIcon = ACTION_CONFIG[log.action]?.icon || Pencil;
                const actionCfg = ACTION_CONFIG[log.action] || ACTION_CONFIG.UPDATE;
                const isExpanded = expandedRows.has(log.id);
                const hasChanges = log.changes && Object.keys(log.changes).length > 0;

                return (
                  <React.Fragment key={log.id}>
                    <TableRow 
                      className={`cursor-pointer hover:bg-slate-50 ${isExpanded ? 'bg-slate-50' : ''}`}
                      onClick={() => hasChanges && toggleRow(log.id)}
                      data-testid={`audit-row-${log.id}`}
                    >
                      <TableCell>
                        {hasChanges && (
                          isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{log.userName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${actionCfg.color} rounded-sm`}>
                          <ActionIcon className="w-3 h-3 mr-1" />
                          {actionCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-sm">
                          {log.recordType}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {log.recordName || log.recordId?.substring(0, 8)}
                      </TableCell>
                    </TableRow>
                    {isExpanded && hasChanges && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-slate-50 p-4">
                          <div className="pl-6">
                            <p className="text-xs font-bold uppercase text-slate-500 mb-2">Changes</p>
                            {renderChanges(log.changes)}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
